import React, { useEffect, useState, Component } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Search, Truck, ArrowRight, Phone, Clock, MapPin } from 'lucide-react';
import { useT } from '../utils/i18n';
import { loadLatestOrder, decodeOrderToken, saveOrderForTracking, TrackedOrder } from '../utils/orderTracking';
// Lightweight entry point for tracking. The actual tracking page lives
// at /track/:orderId. This page (at /track) lets returning customers
// either resume their most recent order (pulled from localStorage) or
// punch in any order ID directly.
export function TrackOrderLookup() {
  const navigate = useNavigate();
  const t = useT();
  const [orderId, setOrderId] = useState('');
  const [recent, setRecent] = useState<TrackedOrder | null>(null);
  useEffect(() => {
    window.scrollTo(0, 0);
    // If the user arrived via a shared tracking link (?d=<token>) hitting /track,
    // decode it, persist it locally, then jump straight to the order page.
    try {
      // Read ?d=<token> from the URL. BrowserRouter puts it in location.search;
      // we still fall back to the hash for any stale shared links from before
      // the router switch.
      const search = window.location.search || (window.location.hash.includes('?') ? window.location.hash.slice(window.location.hash.indexOf('?')) : '');
      if (search) {
        const params = new URLSearchParams(search);
        const token = params.get('d');
        if (token) {
          const decoded = decodeOrderToken(token);
          if (decoded) {
            saveOrderForTracking(decoded);
            navigate(`/track/${encodeURIComponent(decoded.orderId)}?d=${token}`, {
              replace: true
            });
            return;
          }
        }
      }
    } catch {
      /* ignore */
    }
    // Otherwise show the most recent locally-saved order
    const latest = loadLatestOrder();
    if (latest) setRecent(latest);
  }, [navigate]);
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const cleaned = orderId.trim();
    if (!cleaned) return;
    navigate(`/track/${encodeURIComponent(cleaned)}`);
  };
  return <main className="py-12 md:py-20 bg-[#fdfbf7] dark:bg-[#1b1b1b] min-h-[calc(100vh-64px)] transition-colors duration-300" data-id="element-3486" data-mp="24u05v4m7zke6">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-3487" data-mp="29s1lffkft1kw">
        <motion.div initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.4
      }} className="text-center mb-10" data-id="element-3488" data-mp="2xifixllsjsmv">
          <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-[#04A05C]/10 text-[#04A05C] mb-5" data-id="element-3489" data-mp="2nk9fgzkc6efw">
            <Truck className="w-7 h-7" data-id="element-3490" data-mp="2sijdrj1cxv5q" />
          </div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-brand-charcoal dark:text-white mb-3" data-id="element-3491" data-mp="2sl5kxhkc68nf">
            {t('track.title')}
          </h1>
          <p className="text-stone-600 dark:text-stone-400 max-w-md mx-auto" data-id="element-3492" data-mp="1h4ytj6a9np3m">
            {t('track.subtitle')}
          </p>
        </motion.div>

        {/* Recent order card — updated to read from unified TrackedOrder shape */}
        {recent && <motion.div initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.4,
        delay: 0.1
      }} className="mb-8" data-id="element-3493" data-mp="3qf7w2nb2hkty">
            <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-stone-500 dark:text-stone-400 mb-3" data-id="element-3494" data-mp="3clgpnnsmuh2g">
              {t('track.recentOrder')}
            </p>
            <Link to={`/track/${recent.orderId}`} className="group block bg-white dark:bg-[#222] border border-stone-200 dark:border-[#333] rounded-2xl p-5 hover:border-[#04A05C]/40 dark:hover:border-[#04A05C]/40 transition-all shadow-sm hover:shadow-md" data-id="element-3495" data-mp="37k9fxj6xj7t5">
              <div className="flex items-center justify-between gap-4" data-id="element-3496" data-mp="2swekvw5g8rcw">
                <div className="min-w-0 flex-1" data-id="element-3497" data-mp="2ow4eiidqvyxi">
                  <p className="font-heading text-lg font-bold text-brand-charcoal dark:text-white mb-1" data-id="element-3498" data-mp="2ovctu2lorlm0">
                    {t('track.orderNum')}
                    {recent.orderId}
                  </p>
                  {recent.address && <p className="text-sm text-stone-600 dark:text-stone-400 flex items-center gap-1.5 mb-1" data-id="element-3499" data-mp="36hu5fhvjwz8f">
                      <MapPin className="w-3.5 h-3.5 shrink-0" data-id="element-3500" data-mp="3g9zxerlszgnm" />
                      <span className="truncate" data-id="element-3501" data-mp="3gbb00o3iiid5">{recent.address}</span>
                    </p>}
                  {recent.date && <p className="text-xs text-stone-500 dark:text-stone-500 flex items-center gap-1.5" data-id="element-3502" data-mp="3lb5rasr799yq">
                      <Clock className="w-3 h-3 shrink-0" data-id="element-3503" data-mp="3pcr7oo2y06do" />
                      {new Date(recent.date).toLocaleDateString('en-US', {
                  weekday: 'short',
                  month: 'short',
                  day: 'numeric'
                })}
                      {recent.time ? ` · ${recent.time}` : ''}
                    </p>}
                </div>
                <div className="inline-flex items-center gap-1.5 text-[#04A05C] font-bold text-sm shrink-0" data-id="element-3504" data-mp="24vmdtyy31nde">
                  {t('track.continue')}
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" data-id="element-3505" data-mp="1sklo0o04wzbx" />
                </div>
              </div>
            </Link>
          </motion.div>}

        {/* Lookup form */}
        <motion.form initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.4,
        delay: 0.15
      }} onSubmit={submit} className="bg-white dark:bg-[#222] border border-stone-200 dark:border-[#333] rounded-2xl p-6 shadow-sm" data-id="element-3506" data-mp="1imo061m6t4ju">
          <label className="block text-xs font-bold uppercase tracking-wider text-stone-600 dark:text-stone-300 mb-2" data-id="element-3507" data-mp="26b764ahrl58r">
            {recent ? t('track.lookupAnother') : t('track.enterOrder')}
          </label>
          <div className="relative" data-id="element-3508" data-mp="uxwb3j3w92ba">
            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none" data-id="element-3509" data-mp="uuqoqbmngej9">
              <Search className="w-4 h-4 text-stone-400 dark:text-stone-500" data-id="element-3510" data-mp="pzuwpbhexr65" />
            </div>
            <input type="text" value={orderId} onChange={e => setOrderId(e.target.value)} placeholder={t('track.placeholder')} className="w-full pl-10 pr-4 py-3.5 bg-stone-50 dark:bg-[#1a1a1a] border border-stone-200 dark:border-[#444] rounded-lg text-brand-charcoal dark:text-white placeholder-stone-400 dark:placeholder-stone-500 focus:outline-none focus:ring-2 focus:ring-[#04A05C] focus:border-transparent transition-all" autoFocus={!recent} data-id="element-3511" data-mp="3e1kxghb7hrf0" />
          </div>
          <button type="submit" disabled={!orderId.trim()} className="mt-4 w-full bg-[#04A05C] hover:bg-[#048B3A] disabled:opacity-50 disabled:cursor-not-allowed text-white py-3.5 rounded-lg font-bold text-sm transition-colors flex items-center justify-center" data-id="element-3512" data-mp="2z7gd0sqr5xhn">
            {t('track.button')}
            <ArrowRight className="w-4 h-4 ml-2" data-id="element-3513" data-mp="2bhvhzbhbr9s0" />
          </button>
          <p className="text-[11px] text-stone-500 dark:text-stone-500 mt-3 text-center" data-id="element-3514" data-mp="2gfnmr632iqdm">
            {t('track.note')}
          </p>
        </motion.form>

        {/* Help */}
        <div className="mt-8 text-center" data-id="element-3515" data-mp="1wvyhyvjsjeh6">
          <p className="text-sm text-stone-600 dark:text-stone-400 mb-2" data-id="element-3516" data-mp="1stbmmknwvbf8">
            {t('track.help.title')}
          </p>
          <a href="tel:206-619-3742" className="inline-flex items-center gap-2 text-brand-orange font-semibold hover:underline" data-id="element-3517" data-mp="26tw51fpwz587">
            <Phone className="w-4 h-4" data-id="element-3518" data-mp="21vkut3vpispg" />
            {t('track.help.call')}
          </a>
        </div>
      </div>
    </main>;
}